import React, { useState, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import classNames from 'classnames'
import _ from 'lodash'

import contentIcon from 'Assets/images/module-switcher-icons/Icon_Content Library.svg?url'
import workshopIcon from 'Assets/images/module-switcher-icons/Icon_Bot Workshop.svg?url'
import reviewIcon from 'Assets/images/module-switcher-icons/Icon_Bot Review.svg?url'
import settingsIcon from 'Assets/images/module-switcher-icons/Icon_Manange.svg?url'
import routes from 'LegacyUtils/routes'
import hfcIcon from 'Assets/images/happyfox.svg?url'
import externalLink from 'Assets/images/external-link.svg?url'
import { getAccountFeatures, getAccountDetails } from 'Selectors/account'

const ModuleSwitcher = (props) => {
  const [showModuleSwitcher, setShowModuleSwitcher] = useState(false)

  const { showOverlay } = props

  // For setting small delays to prevent opening/closing module switcher by accident
  const showTimer = useRef(null)
  const hideTimer = useRef(null)

  const accountFeatures = useSelector(getAccountFeatures)
  const accountDetails = useSelector(getAccountDetails)

  const closeModuleSwitcher = () => {
    clearInterval(showTimer.current)
    hideTimer.current = setTimeout(() => {
      setShowModuleSwitcher(false)
      showOverlay(false)
    }, 100)
  }
  const params = useParams()

  const openModuleSwitcher = () => {
    clearInterval(hideTimer.current)
    showTimer.current = setTimeout(() => {
      setShowModuleSwitcher(true)
      showOverlay(true)
    }, 200)
  }

  const isOptEnabled = useCallback(
    (optionName) => {
      const optionsFlags = _.get(accountFeatures, 'flowBuilder.options')
      return optionsFlags ? optionsFlags[optionName] === true : true
    },
    [accountFeatures]
  )

  const isFeatureEnabled = useCallback(
    (optionName) => {
      const isFeatEnabled = accountFeatures?.[optionName]

      const insightSettings = _.get(accountDetails, 'insight_settings')
      const isInsightEnabled = insightSettings && Object.keys(insightSettings).length !== 0

      if (['knowledgeBase', 'training'].includes(optionName)) {
        return (isFeatEnabled ?? true) && isInsightEnabled
      }

      return isFeatEnabled ?? false
    },
    [accountFeatures, accountDetails]
  )

  return (
    <React.Fragment>
      <div
        className={classNames('nav-link dropdown-toggle text-right text-white', { expanded: showModuleSwitcher })}
        href='#'
        id='userDropdown'
        role='button'
        aria-haspopup='true'
        onMouseOver={openModuleSwitcher}
        onMouseOut={closeModuleSwitcher}
        style={{ display: 'flex', alignItems: 'center', fontSize: '16px', padding: '1rem 0.5rem' }}
      >
        <img
          src={routes[params.tab] ? routes[params.tab].icon : routes.dashboard.icon}
          className='mr-2 nav-icon'
          alt=''
        />
        {routes[params.tab] ? routes[params.tab].label : routes.dashboard.label}
        <div
          className={classNames('dropdown-menu dropdown-menu-lg-left module-switcher', { show: showModuleSwitcher })}
          aria-labelledby='module switcher'
        >
          <div className='module-switcher-column'>
            <div className='dropdown-item heading'>
              <img className='mr-3 module-switcher-icon' src={workshopIcon} alt='' /> Bot Workshop
            </div>
            <Link className='dropdown-item' to='/dashboard' onClick={closeModuleSwitcher}>
              Flow Builder
            </Link>
            <Link className='dropdown-item disabled d-none' to='/dashboard'>
              Troubleshooters
            </Link>
            <Link className='dropdown-item disabled d-none' to='/dashboard'>
              Feedback Templates
            </Link>
          </div>
          <div className='module-switcher-column'>
            <div className='dropdown-item heading'>
              <img className='mr-3 module-switcher-icon' src={contentIcon} alt='' /> Content Library
            </div>
            <Link
              className={classNames('dropdown-item', { disabled: !isOptEnabled('guide') })}
              to='/guides'
              onClick={closeModuleSwitcher}
            >
              Guides
            </Link>
            <Link
              className={classNames('dropdown-item', { disabled: !isOptEnabled('answer') })}
              to='/answers'
              onClick={closeModuleSwitcher}
            >
              Answers
            </Link>
            <Link
              className={classNames('dropdown-item', { disabled: !isFeatureEnabled('knowledgeBase') })}
              to='/knowledge-base'
              onClick={closeModuleSwitcher}
            >
              Knowledge Base
            </Link>
          </div>

          <div className='module-switcher-column'>
            <div className='dropdown-item heading'>
              <img className='mr-3 module-switcher-icon' src={reviewIcon} alt='' /> Bot Review
            </div>
            <Link className={classNames('dropdown-item', { disabled: !isFeatureEnabled('training') })} to='/training'>
              Training
            </Link>
            <Link className='dropdown-item disabled' to='/dashboard'>
              Reports
            </Link>
          </div>
          <div className='module-switcher-column'>
            <div className='dropdown-item heading'>
              <img className='mr-3 module-switcher-icon' src={settingsIcon} alt='' /> Manage
            </div>
            <Link
              className={classNames('dropdown-item', { disabled: !isFeatureEnabled('campaign') })}
              to='/campaigns'
              onClick={closeModuleSwitcher}
            >
              Campaigns
            </Link>
            <Link className='dropdown-item' to='/users' onClick={closeModuleSwitcher}>
              Users
            </Link>
            <Link className='dropdown-item disabled d-none' to='/dashboard'>
              Company Settings
            </Link>
          </div>
          <div className='nav-menu__footer'>
            <div className='nf-link'>
              <a href='https://www.happyfox.com/' target='_blank' rel='noopener noreferrer'>
                <img src={hfcIcon} className='hf-logo' /> Help Desk <img className='ext-link' src={externalLink} />
              </a>
            </div>
            <div className='nf-link'>
              <a href='https://www.happyfox.com/live-chat/' target='_blank' rel='noopener noreferrer'>
                <img src={hfcIcon} className='hf-logo' /> Chat <img className='ext-link' src={externalLink} />
              </a>
            </div>
            <div className='nf-link'>
              <a href='https://www.happyfox.com/business-intelligence/' target='_blank' rel='noopener noreferrer'>
                <img src={hfcIcon} className='hf-logo' /> BI <img className='ext-link' src={externalLink} />
              </a>
            </div>
            <div className='nf-link'>
              <a href='https://www.happyfox.com/workflows/' target='_blank' rel='noopener noreferrer'>
                <img src={hfcIcon} className='hf-logo' /> Workflows <img className='ext-link' src={externalLink} />
              </a>
            </div>
            <div className='nf-link'>
              <a href='https://www.happyfox.com/in-app-chat/' target='_blank' rel='noopener noreferrer'>
                <img src={hfcIcon} className='hf-logo' /> In-App Chat <img className='ext-link' src={externalLink} />
              </a>
            </div>
            <div className='nf-link'>
              <a href='https://www.happyfox.com/assist-ai/' target='_blank' rel='noopener noreferrer'>
                <img src={hfcIcon} className='hf-logo' /> Assist AI <img className='ext-link' src={externalLink} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default ModuleSwitcher
